/**
 * Per-group discount % used as employee commission on sales.
 * Stored on groups as employeeCommissionPercent (falls back to product discount).
 */
import { collection, doc, getDocs, updateDoc, writeBatch } from "firebase/firestore";
import { db } from "../firebase";

/**
 * Resolve commission rate % for a group (0–100).
 * Prefers employeeCommissionPercent when set; else product discount.
 */
export function resolveGroupCommissionPercent(groupDoc) {
  if (!groupDoc || typeof groupDoc !== "object") return 0;
  if (
    groupDoc.employeeCommissionPercent != null &&
    groupDoc.employeeCommissionPercent !== "" &&
    Number.isFinite(Number(groupDoc.employeeCommissionPercent))
  ) {
    return Number(groupDoc.employeeCommissionPercent);
  }
  const d = Number(groupDoc.discount);
  if (Number.isFinite(d) && d >= 0) return d;
  return 0;
}

/** Display % for settings UI when field missing (show discount as starting value). */
export function displayGroupCommissionPercent(groupDoc) {
  if (!groupDoc) return 0;
  if (
    groupDoc.employeeCommissionPercent != null &&
    groupDoc.employeeCommissionPercent !== "" &&
    Number.isFinite(Number(groupDoc.employeeCommissionPercent))
  ) {
    return Number(groupDoc.employeeCommissionPercent);
  }
  return Number(groupDoc.discount || 0);
}

export async function loadGroupsForCommissionSettings() {
  const snap = await getDocs(collection(db, "groups"));
  return snap.docs
    .map((d) => {
      const data = d.data() || {};
      const name = String(data.name ?? data.gname ?? data.group ?? data.groupName ?? "").trim();
      return {
        id: d.id,
        name,
        nameLower: (data.nameLower || name).toLowerCase(),
        discount: Number(data.discount || 0),
        employeeCommissionPercent:
          data.employeeCommissionPercent != null && data.employeeCommissionPercent !== ""
            ? Number(data.employeeCommissionPercent)
            : null,
      };
    })
    .filter((g) => g.name)
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
}

/**
 * Map lowercased group name → commission % for report calc.
 */
export function buildCommissionRateMap(groups) {
  /** @type {Map<string, number>} */
  const map = new Map();
  for (const g of groups || []) {
    const key = String(g.name || "").trim().toLowerCase();
    if (!key) continue;
    map.set(key, resolveGroupCommissionPercent(g));
  }
  return map;
}

export async function saveGroupCommissionPercent(groupId, percent) {
  const n = Number(percent);
  if (!groupId || !Number.isFinite(n) || n < 0 || n > 100) {
    throw new Error("Commission % must be between 0 and 100");
  }
  await updateDoc(doc(db, "groups", groupId), {
    employeeCommissionPercent: n,
    updatedAt: new Date(),
  });
}

/**
 * @param {{ id: string, percent: number }[]} rows
 */
export async function saveManyGroupCommissionPercents(rows) {
  const list = (rows || []).filter((r) => r?.id);
  if (!list.length) return 0;

  const CHUNK = 400;
  let saved = 0;
  for (let i = 0; i < list.length; i += CHUNK) {
    const batch = writeBatch(db);
    const slice = list.slice(i, i + CHUNK);
    for (const row of slice) {
      const n = Number(row.percent);
      if (!Number.isFinite(n) || n < 0 || n > 100) {
        throw new Error(`Invalid commission % for group ${row.id}`);
      }
      batch.update(doc(db, "groups", row.id), {
        employeeCommissionPercent: n,
        updatedAt: new Date(),
      });
    }
    await batch.commit();
    saved += slice.length;
  }
  return saved;
}
